import { useCallback, useEffect, useState } from 'react';
import { History, RotateCcw, User, Calendar, ArrowRight } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { flujoCajaApi } from '../../lib/api';
import { toast } from '../../hooks/use-toast';

const fmtCLP = (v) =>
  new Intl.NumberFormat('es-CL', { maximumFractionDigits: 0 }).format(Math.abs(v));

const fmtFechaCorta = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso + 'T00:00:00');
  const dia = ['do', 'lu', 'ma', 'mi', 'ju', 'vi', 'sá'][d.getDay()];
  const mes = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'][d.getMonth()];
  return `${dia} ${d.getDate()} ${mes}`;
};

const fmtDateTimeAgo = (iso) => {
  if (!iso) return '';
  const now = new Date();
  const d = new Date(iso);
  const mins = Math.floor((now - d) / 60000);
  if (mins < 60) return `hace ${mins}m`;
  if (mins < 60 * 24) return `hace ${Math.floor(mins / 60)}h`;
  return `hace ${Math.floor(mins / 1440)}d`;
};

/**
 * Popover con historial de grid-shifts (movimientos de fecha en el grid).
 * Incluye botón "Deshacer" por cada shift.
 */
export function GridShiftsHistory({ onChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmUndo, setConfirmUndo] = useState(null); // shift
  const [undoBusy, setUndoBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await flujoCajaApi.listGridShifts();
      setItems(res.data?.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doUndo = async () => {
    if (!confirmUndo) return;
    setUndoBusy(true);
    try {
      await flujoCajaApi.deleteGridShift(confirmUndo.id);
      toast({
        title: 'Movimiento revertido',
        description: `${confirmUndo.nota || `Fila ${confirmUndo.orden}`} volvió a ${fmtFechaCorta(confirmUndo.fecha_origen)}`,
      });
      setConfirmUndo(null);
      await load();
      onChange?.();
    } catch (err) {
      toast({
        title: 'No se pudo revertir',
        description: err?.response?.data?.detail || 'Error',
        variant: 'destructive',
      });
    } finally {
      setUndoBusy(false);
    }
  };

  return (
    <>
      <Popover onOpenChange={(o) => { if (o) load(); }}>
        <PopoverTrigger asChild>
          <Button size="sm" variant="outline" className="gap-1.5 h-8" data-testid="btn-grid-shifts-history">
            <History className="w-3.5 h-3.5" />
            Historial mov.
            {items.length > 0 && (
              <span className="ml-1 rounded-full bg-blue-600 text-white text-[10px] font-bold px-1.5 py-0.5 tabular-nums">
                {items.length}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[480px] p-0" align="end" data-testid="popover-grid-shifts">
          <div className="border-b px-3 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <History className="w-4 h-4 text-blue-600" />
              <p className="text-sm font-semibold">Historial de movimientos</p>
            </div>
            <span className="text-[11px] text-muted-foreground">{items.length} total</span>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading && <p className="px-3 py-4 text-sm text-muted-foreground">Cargando…</p>}
            {!loading && items.length === 0 && (
              <p className="px-3 py-6 text-sm text-muted-foreground text-center">
                Sin movimientos registrados.
              </p>
            )}
            {items.map((s) => {
              const isBigShift = Math.abs(s.dias_shift) >= 14;
              const isBackward = s.dias_shift < 0;
              return (
                <div
                  key={s.id}
                  className="border-b last:border-b-0 px-3 py-2 hover:bg-muted/40 transition-colors"
                  data-testid={`shift-${s.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                        <span className="px-1 rounded bg-slate-200 dark:bg-slate-700 font-mono text-[10px]">
                          fila {s.orden}
                        </span>
                        <span className="truncate">{s.nota || '—'}</span>
                      </p>
                      <div className="flex items-center gap-1 mt-0.5 text-sm">
                        <Calendar className="w-3 h-3 text-muted-foreground" />
                        <span className="tabular-nums">{fmtFechaCorta(s.fecha_origen)}</span>
                        <ArrowRight className="w-3 h-3 text-muted-foreground" />
                        <span className={`tabular-nums font-semibold ${isBigShift ? 'text-amber-700' : isBackward ? 'text-red-700' : 'text-emerald-700'}`}>
                          {fmtFechaCorta(s.fecha_destino)}
                        </span>
                        <span className={`text-[10px] px-1 py-0 rounded tabular-nums ${isBigShift ? 'bg-amber-100 text-amber-800' : isBackward ? 'bg-red-100 text-red-800' : 'bg-emerald-100 text-emerald-800'}`}>
                          {s.dias_shift > 0 ? '+' : ''}{s.dias_shift}d
                        </span>
                        {s.auto_skipped && (
                          <span
                            className="text-[10px] px-1 py-0 rounded bg-blue-100 text-blue-800"
                            title="Destino caía en sábado/domingo — auto-ajustado al lunes"
                          >
                            auto→L
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-muted-foreground flex items-center gap-2 mt-0.5">
                        <span className="tabular-nums font-medium text-gray-700">${fmtCLP(s.monto)}</span>
                        <span>·</span>
                        <User className="w-3 h-3" />
                        <span className="truncate max-w-[140px]">{s.created_by || '—'}</span>
                        <span>·</span>
                        <span>{fmtDateTimeAgo(s.created_at)}</span>
                      </p>
                    </div>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-red-600 hover:bg-red-50 shrink-0"
                      title="Deshacer este movimiento"
                      onClick={() => setConfirmUndo(s)}
                      data-testid={`btn-undo-${s.id}`}
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="border-t px-3 py-1.5 text-[10px] text-muted-foreground bg-muted/30">
            El historial muestra los últimos 100 movimientos. Deshacer revierte el shift
            inmediatamente en el grid.
          </div>
        </PopoverContent>
      </Popover>

      {/* Dialog confirmar undo */}
      <AlertDialog open={!!confirmUndo} onOpenChange={(o) => !o && !undoBusy && setConfirmUndo(null)}>
        <AlertDialogContent data-testid="dialog-undo-shift">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Deshacer este movimiento?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-sm space-y-1">
                <p className="font-medium">{confirmUndo?.nota || `Fila ${confirmUndo?.orden}`}</p>
                <p className="text-xs text-muted-foreground">
                  Volverá <span className="font-semibold">{fmtFechaCorta(confirmUndo?.fecha_destino)}</span> → <span className="font-semibold">{fmtFechaCorta(confirmUndo?.fecha_origen)}</span>
                  {' '}· ${confirmUndo ? fmtCLP(confirmUndo.monto) : ''}
                </p>
                <p className="text-[11px] text-muted-foreground mt-2">
                  El grid se recalcula inmediatamente.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={undoBusy} data-testid="btn-cancel-undo">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); doUndo(); }}
              disabled={undoBusy}
              className="bg-red-600 hover:bg-red-700"
              data-testid="btn-confirm-undo"
            >
              {undoBusy ? 'Revirtiendo…' : 'Sí, deshacer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default GridShiftsHistory;
